import {
	Box,
	Button,
	Drawer,
	DrawerContent,
	DrawerOverlay,
	FormControl,
	FormLabel,
	Heading,
	Input,
	Text,
	Textarea,
	useBreakpointValue,
	useToast,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import InputCustom from '../InputCustom';
import { CalendarIcon } from '@chakra-ui/icons';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import { UIErrorResponse } from 'types/src/error';
import { useMutation } from 'react-query';
import { createRequestCallback } from '../../../apis/V2/requestCallback';
import ModalTemplateV2 from '../ModalTemplateV2';
import ModalTemplate from '../ModalTemplate';
import { useGetRequestCallback } from '../../../hooks/V2/useRequestCallback';

interface Props {
	sourceType: string;
	openCallbackDrawer: boolean;
	setOpenCallbackDrawer: (v: boolean) => void;
}

interface FormInputs {
	date: string;
	query: string;
}

const RequestCallback = ({ sourceType, openCallbackDrawer, setOpenCallbackDrawer }: Props) => {
	const [showSuccess, setShowSuccess] = useState(false);

	const isMobile = useBreakpointValue({ base: true, md: false });

	return (
		<>
			{!isMobile ? (
				<ModalTemplate
					title=""
					marginX={2}
					bgColor="white"
					maxWidth="500px"
					showCloseButton={true}
					showCloseButtonIcon={true}
					isOpen={openCallbackDrawer}
					modalContentProps={{ shadow: 'none', p: '0px' }}
					modalOverlayProps={{ bg: 'rgba(1, 10, 27, 0.80)' }}
					onClose={() => setOpenCallbackDrawer(false)}
				>
					<Box position="relative" overflow="hidden" rounded="24px">
						<RequestCallbackForm sourceType={sourceType} setOpenCallbackDrawer={setOpenCallbackDrawer} setShowSuccess={setShowSuccess} />
					</Box>
				</ModalTemplate>
			) : (
				<Drawer
					placement="bottom"
					returnFocusOnClose={false}
					isOpen={openCallbackDrawer}
					onClose={() => setOpenCallbackDrawer(false)}
					onOverlayClick={() => setOpenCallbackDrawer(false)}
				>
					<DrawerOverlay bg="rgba(0, 0, 0, 0.60)" />
					<DrawerContent roundedTop="40px" overflow="hidden" maxH="70%">
						<RequestCallbackForm sourceType={sourceType} setOpenCallbackDrawer={setOpenCallbackDrawer} setShowSuccess={setShowSuccess} />
					</DrawerContent>
				</Drawer>
			)}
			<ModalTemplateV2
				isOpen={showSuccess}
				closeOnOverlayClick={true}
				onClose={() => {
					setShowSuccess(false);
				}}
				autoCloseInSeconds={4}
				showConfetti={false}
				text="Wohoo! Your free counselling session has been scheduled!"
				extra={
					<Text mt="21px" mx="auto" textAlign="center" maxW="472px" color="#E5E5E5" textStyle={{ base: 'subtitle1', md: 'subtitle1' }}>
						Our team will get back to you
					</Text>
				}
			/>
		</>
	);
};

export default RequestCallback;

const RequestCallbackForm = ({
	setOpenCallbackDrawer,
	setShowSuccess,
	sourceType,
}: {
	setOpenCallbackDrawer: (v: boolean) => void;
	setShowSuccess: (v: boolean) => void;
	sourceType: string;
}) => {
	const toast = useToast();
	const today = new Date().toISOString().slice(0, new Date().toISOString().lastIndexOf(':'));
	const { refetch: getCallbacksDataRefetch } = useGetRequestCallback(sourceType);
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<FormInputs>();

	const { mutate: createCallbackMutate, isLoading: isCreateCallbackMutateLoading } = useMutation(createRequestCallback, {
		onSuccess: data => {
			console.log('data:', data);
			setOpenCallbackDrawer(false);
			setShowSuccess(true);
			getCallbacksDataRefetch();
			// toast({
			// 	title: 'Success',
			// 	description: 'Password reset link sent to your email',
			// 	status: 'success',
			// 	duration: 2000,
			// 	isClosable: true,
			// 	position: 'top',
			// });
		},
		onError: (error: AxiosError<UIErrorResponse>) => {
			toast({
				title: 'Error',
				description: error?.response?.data?.message || 'Password reset failed',
				status: 'error',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		},
	});

	const onSubmit: SubmitHandler<FormInputs> = data => {
		createCallbackMutate({ date: data.date, query: data.query, source: sourceType });
	};

	return (
		<Box as="form" bg="#FFF9E5" h="full" pb={{ base: '100px', md: '0px' }} overflow="auto" position="relative" onSubmit={handleSubmit(onSubmit)}>
			<Box display={{ base: 'block', md: 'none' }} h="3px" rounded="4px" bg="#595959" maxW="56px" mx="auto" mt="24px"></Box>

			<Text textStyle="body2" textAlign="center" mt="16px">
				Have any queries?
			</Text>

			<Text mt="10px" as="h4" textStyle="h4" textAlign="center" color="#4358F6">
				Book a Free Counselling Session with our Industry Experts
			</Text>

			<Box mt="22px" px="16px" display="flex" gap="16px" flexDir="column">
				<FormControl id="email" isInvalid={errors.date ? true : false}>
					<Text as={FormLabel} textStyle="body2-md">
						Select Date and Time*
					</Text>
					<Input min={today} bg="white" type="datetime-local" placeholder="--/--" {...register('date', { required: 'Date is required' })} />
					{errors.date && (
						<Text textStyle="body2" mt="6px" color="#D61E27">
							{errors.date.message}
						</Text>
					)}
				</FormControl>

				<FormControl id="email" isInvalid={errors.query ? true : false}>
					<Text as={FormLabel} textStyle="body2-md">
						Do you have any query?
					</Text>
					<Textarea bg="white" h="133px" placeholder="Mention your query..." {...register('query', { required: false })} />
					{errors.query && (
						<Text textStyle="body2" mt="6px" color="#D61E27">
							{errors.query.message}
						</Text>
					)}
				</FormControl>
			</Box>

			<Box
				w="full"
				bottom="0px"
				p="12px 14px"
				mt={{ base: '0px', md: '24px' }}
				bg={{ base: 'white', md: 'none' }}
				position={{ base: 'fixed', md: 'relative' }}
				boxShadow={{ base: '0px -8px 32px 0px rgba(0, 0, 0, 0.10)', md: 'none' }}
			>
				<Button isLoading={isCreateCallbackMutateLoading} type="submit" w="full" variant="primary" bg="#4358F6">
					schedule
				</Button>
			</Box>
		</Box>
	);
};
